import { Alert, Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import MainLayout from "../../layout/MainLayout";
import { useParams } from "react-router";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Overlay } from "../../components/Common/Overlay";
import AuthAPI from "../../api/endpoints/Auth";
import { useAppDispatch } from "../../store/hooks";
import { setAuthStatus, setSession } from "../../store/features/session/userSession";
import ModalSupport from "../../components/Common/ModalSupport";

const GetURLParameters = () => {
    const str = window.location.href.split('?');
    if (str.length < 2) return null;
    const query = str[1];
    if (!query) return null;
    const urlParams = new URLSearchParams(query);
    return urlParams;
};

const PassCodeHint = ({
    publicInformation,
}: {
    publicInformation: any;
}) => {
    const [hinstDisplayStatus, setHintDisplayStatus] = useState(false);
    if (!publicInformation) return null;
    const { hint } = publicInformation;
    if (!hint) return null;
    return (
        <div className="d-flex flex-column">
            <div className="text-center border border-bottom-0">
                <Button
                    variant="link"
                    className="p-0 w-100"
                    onMouseDown={() => setHintDisplayStatus(true)}
                >Need a clue?</Button>
            </div>
            <div className="flex-grow-1 text-center">
                {hinstDisplayStatus ?
                    <Alert
                        variant="dark"
                        className="px-2 py-1 border text-muted h-100 fw-semibold">
                        {hint}
                    </Alert> :
                    <Alert
                        variant="default"
                        className="px-2 py-1 border text-muted h-100">
                        <span className="fw-light text-muted text-italic">Hidden</span>
                    </Alert>
                }
            </div>
        </div>
    )
}

const TextMessageForm = forwardRef(({
    publicInformation,
    sharedBoxId, randomKey
}: {
    publicInformation: any;
    sharedBoxId: string;
    randomKey: string;
}, ref: any) => {
    const [passwordAsked, setPasswordAskedStatus] = useState(false);
    const [sendingCode, setSendingCodeStatus] = useState(false);
    const handleSendCode = async () => {
        setSendingCodeStatus(true);
        try {
            await AuthAPI.requestOTP(sharedBoxId, randomKey);
        } catch (e: any) {
            console.warn(e);
        }
        setPasswordAskedStatus(true);
        setSendingCodeStatus(false);
    };
    if (!publicInformation) return null;
    return (
        <>
            <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">One Time Password / OTP Code</Form.Label>
                <Form.Control type="password" ref={ref} />
            </Form.Group>
            <Alert className="text-center py-2" variant="secondary">
                {passwordAsked ?
                    <div className="fw-light d-block">Didn't receive your code?</div> :
                    <>
                        <div className="fw-light d-block mb-3">Click on the button below to get your OTP Code.</div>
                        <div className="fw-light d-block mb-3">It will be send to <span className="fw-semibold">{publicInformation.phoneNumber}</span></div>
                    </>
                }
                <Button
                    className="d-block w-100"
                    variant="success"
                    onClick={handleSendCode}
                >{sendingCode ? 'Sending...' : 'Request OTP Code '}</Button>
            </Alert>
        </>
    );
});


export const LoginPage = () => {
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [serverError, setServerError] = useState<any>(null);
    const [pInformation, setPublicInformation] = useState<any>(null);
    const [sharedBoxRandomKey, setRandomKey] = useState<any>(null);
    const [supportModalStatus, setSupportModalStatus] = useState<boolean>(false);
    const { sharedBoxId } = useParams();
    const fieldRef = {
        username: useRef(null),
        passcode: useRef(null),
    };
    const handleAuth = async (e: any) => {
        setServerError(null);
        setIsLoading(true);
        try {
            if (e && e.preventDefault) {
                e.preventDefault();
                e.target.blur();
            }
            const body = {
                sharedBoxId,
                username: (fieldRef.username.current as any).value,
                password: (fieldRef.passcode.current as any).value,
            };
            const result = await AuthAPI.auth(body);
            dispatch(setSession(result));
            dispatch(setAuthStatus(true));
        } catch (e: any) {
            console.error(e);
            let errorData = {
                code: 'server-error',
            };
            if (e && e.response && e.response.data && e.response.data) {
                const errorResponse = e.response.data;
                errorData = {
                    code: errorResponse.information || 'server-error',
                };
            }
            setServerError(errorData);
        }
        setIsLoading(false);
    };
    const handleGetPublicInformation = async () => {
        setIsLoading(true);
        try {
            if (!sharedBoxId) throw new Error('no-information');
            const urlParams = GetURLParameters();
            if (!urlParams) throw new Error('no-information');
            const randomKey = urlParams.get('randomKey');
            if (!randomKey) throw new Error('no-information');
            setRandomKey(randomKey);
            const publicInfo = await AuthAPI.getPublicInformation(sharedBoxId, randomKey);
            setPublicInformation(publicInfo);
        } catch (e: any) {
            setPublicInformation(null);
        }
        setIsLoading(false);
    }
    useEffect(() => {
        handleGetPublicInformation();
    }, []);
    const hasPasscode = (pInformation && pInformation.securityType === 'PASSCODE');
    const hasTextMessage = (pInformation && pInformation.securityType === 'TEXT_MESSAGE');
    const incompleteAuthSystem = (!hasPasscode && !hasTextMessage);
    return (
        <MainLayout showTopBar={false}>
            <Container className="flex-grow-1">
                <Row className="justify-content-center h-100">
                    <Col
                        xs={12} sm={12} md={6}
                        lg={6} xl={5} xxl={4}
                        className="pt-5"
                    >
                        <img src="/img/fiducial-securemail-logo.png" alt="" className="w-100"/>
                        {sharedBoxId ?
                            <>
                                <Card>
                                    <Card.Body className="position-relative">
                                        {serverError ?
                                            <Alert variant="warning">
                                                {serverError.code === 'server-error' ? 'Server error, please try again later' : 'Invalid username or passcode'}
                                            </Alert> : null}
                                        {incompleteAuthSystem ?
                                            <Alert variant="warning" className="mb-0">
                                                You must use the link provided by email to access this page.
                                            </Alert> :
                                            <>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-semibold">Email Address</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        ref={fieldRef.username}
                                                    />
                                                </Form.Group>
                                                {hasPasscode ?
                                                    <>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label className="fw-semibold">Passcode</Form.Label>
                                                            <Form.Control
                                                                type="password"
                                                                ref={fieldRef.passcode}
                                                            />
                                                        </Form.Group>
                                                        <PassCodeHint publicInformation={pInformation} />
                                                    </> : null
                                                }
                                                {hasTextMessage ?
                                                    <>
                                                        {pInformation ?
                                                            <TextMessageForm
                                                                sharedBoxId={sharedBoxId}
                                                                randomKey={sharedBoxRandomKey}
                                                                publicInformation={pInformation}
                                                                ref={fieldRef.passcode}
                                                            /> : null
                                                        }
                                                    </> : null
                                                }
                                                <div className="d-block">
                                                    <Button
                                                        onClick={handleAuth}
                                                        className="w-100"
                                                    >Connect</Button>
                                                </div>
                                                {isLoading ? <Overlay label="Please wait" /> : null}
                                            </>
                                        }
                                    </Card.Body>
                                </Card>
                                <div className="d-flex justify-content-center">
                                    <Button
                                        variant="link"
                                        onClick={(e: any) => {
                                            e.target.blur();
                                            setSupportModalStatus(true);
                                        }}
                                    >Need support?</Button>
                                </div>
                            </> :
                            <Alert
                                variant="warning"
                                className="m-0"
                            >
                                <div className="h4 fw-semibold mb-3">Unable to find your invitation</div>
                                <p className="fw-light">
                                    Please try to open the invitation link again.
                                </p>
                                <p>
                                    If you keep getting this error, please contact the person who invited you to get a new invitation link.
                                </p>
                            </Alert>
                        }
                    </Col>
                </Row>
            </Container>
            {supportModalStatus ?
                <ModalSupport
                    supportModalStatus={supportModalStatus}
                    setSupportModalStatus={setSupportModalStatus}
                /> : null
            }
        </MainLayout>
    );
};

export default LoginPage;